import { Box, Button, Paper, PaperProps, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Blog } from '../../interfaces/Blog/Blog';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { settingsAtom } from '../../atoms/Settings';

type BlogCardProps = PaperProps & {
  blog: Blog;
};

export const BlogCard = ({ blog, ...props }: BlogCardProps) => {
  const t = useLocalizedText();

  const { primary } = useThemeColor();

  const navigate = useNavigate();

  const settings = useRecoilValue(settingsAtom);

  return (
    <Paper
      {...props}
      onClick={() => navigate(`/blog/${blog.slug}`)}
      sx={{
        borderRadius: '8px',
        flex: '1 1 0',
        maxWidth: 250,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        cursor: 'pointer',
        ...props.sx,
        ...settings.settingsV2?.blogsWidgetSettings?.blogCardSettings
          ?.rootStyle,
      }}
    >
      <Box sx={{ maxHeight: 200, overflow: 'hidden' }}>
        <Box
          component='img'
          src={blog.coverImage}
          alt={blog.title}
          sx={{
            width: '100%',
            height: 'auto',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            display: 'block', // ensures no unexpected whitespace
            ...settings.settingsV2?.blogsWidgetSettings?.blogCardSettings
              ?.imageStyle,
          }}
        />
      </Box>

      <Box
        sx={{
          px: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flexGrow: 1,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            ...settings.settingsV2?.blogsWidgetSettings?.blogCardSettings
              ?.titleStyle,
          }}
        >
          {blog.title}
        </Typography>

        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            ...settings.settingsV2?.blogsWidgetSettings?.blogCardSettings
              ?.descriptionStyle,
          }}
        >
          {blog.shortDescription}
        </Typography>

        <Button
          sx={{
            width: 'fit-content',
            px: 0,
            mt: 'auto',
            color: primary,
            textTransform: 'none',
            ...settings.settingsV2?.blogsWidgetSettings?.blogCardSettings
              ?.readMoreButtonStyle,
          }}
        >
          {t('blog.read_more')}
        </Button>
      </Box>
    </Paper>
  );
};
